<script setup lang="ts">
import {
  PaginationEllipsis,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev,
  PaginationRoot,
} from 'reka-ui';
import Button from '../buttons/button/button.vue';
import IconButton from '../buttons/icon-button/icon-button.vue';
import { IconButtonShape } from '../buttons/icon-button/icon-button.constants';
import {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
} from '../buttons/buttons.constants';
import Icon from '../icon/icon.vue';
import { IconName, IconSize } from '../icon/icon.constants';

type Props = {
  /** Items shown on one page. */
  perPage: number;
  /** Total number of items across every page. */
  totalCount: number;
};

const props = defineProps<Props>();

const page = defineModel<number>({ required: true });

const pageColor = (value: number) =>
  value === page.value ? ButtonColor.PRIMARY : ButtonColor.SECONDARY;
</script>

<template>
  <PaginationRoot
    v-model:page="page"
    :items-per-page="props.perPage"
    :total="props.totalCount"
    :sibling-count="1"
    show-edges
  >
    <PaginationList v-slot="{ items }" class="inline-flex items-center gap-sm">
      <PaginationPrev as-child>
        <IconButton
          :icon-name="IconName.CHEVRON_LEFT"
          label="Previous Page"
          :size="ButtonSize.SM"
          :variant="ButtonVariant.TEXT"
          :color="ButtonColor.SECONDARY"
          :shape="IconButtonShape.ROUNDED"
        />
      </PaginationPrev>
      <template v-for="(item, index) in items" :key="index">
        <PaginationListItem
          v-if="item.type === 'page'"
          :value="item.value"
          as-child
        >
          <Button
            :label="String(item.value)"
            :size="ButtonSize.SM"
            :variant="ButtonVariant.OUTLINED"
            :color="pageColor(item.value)"
            compact
          />
        </PaginationListItem>
        <PaginationEllipsis
          v-else
          aria-hidden="true"
          class="inline-flex h-[2rem] w-[2rem] items-center justify-center text-secondary"
        >
          <Icon :name="IconName.ELLIPSIS" :size="IconSize.MD" />
        </PaginationEllipsis>
      </template>
      <PaginationNext as-child>
        <IconButton
          :icon-name="IconName.CHEVRON_RIGHT"
          label="Next Page"
          :size="ButtonSize.SM"
          :variant="ButtonVariant.TEXT"
          :color="ButtonColor.SECONDARY"
          :shape="IconButtonShape.ROUNDED"
        />
      </PaginationNext>
    </PaginationList>
  </PaginationRoot>
</template>
